import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { useFloatingPanel } from '@/components/admin/protocoles/step/floating-panel';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import type { CriterionCard } from '@matter_extractor/shared';
import { TextField } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * « Ajouter un critère »: the library, filtered down to what this objective can
 * still cite (refonte modulaire §7.2).
 *
 * Three things are left out of the list, and each is a refusal avoided rather
 * than a preference:
 *
 * - a criterion this objective ALREADY cites, because the two rows would carry
 *   the same `data-criterion-id` and the second would only be a way to disagree
 *   with the first about its version;
 * - a RETIRED criterion (§4), because citing one is a warning on the protocol's
 *   card and a refusal at « Passer en production » - offering it here would be
 *   offering a defect;
 * - nothing else: the retired ones are the only rows the library hides, and the
 *   search is on the name alone, which is what an operator has in mind.
 *
 * The chosen criterion joins under `production`, the default tag of every
 * selector (§4); the row it lands on is where the version is forced.
 */

/** 380 px, ce que la classe `w-95` posait. */
const CHOOSER_WIDTH = 380;

export interface CriterionChooserProps {
  /** The whole library, retired entries included: they are filtered here. */
  criteria: readonly CriterionCard[];
  /** What this objective already cites - one criterion, one row. */
  citedIds: readonly string[];
  /**
   * Le bouton « Ajouter un critère », dont ce panneau prend la mesure.
   *
   * Il était `absolute` dans la ligne, donc rogné par l'`overflow-hidden` de
   * la liste d'objectifs - et c'est LUI le plus exposé des trois panneaux de
   * la ligne, puisqu'il est le dernier élément du corps d'un objectif et que
   * son panneau descend donc le plus bas. L'en-tête de
   * `criterion-requirement-row.tsx` déclarait ce motif cassé deux blocs plus
   * haut ; une revue a vu qu'il l'était resté ici.
   */
  anchor: HTMLElement | null;
  onClose: () => void;
  onPick: (criterionId: string) => void;
}

export function CriterionChooser({
  criteria,
  citedIds,
  anchor,
  onPick,
  onClose,
}: CriterionChooserProps) {
  const { panelRef, at } = useFloatingPanel({ anchor, width: CHOOSER_WIDTH, onClose });
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const cited = new Set(citedIds);
  const offered = criteria.filter(
    (card) =>
      card.retiredAt === null &&
      !cited.has(card.id) &&
      (needle === '' || card.name.toLowerCase().includes(needle)),
  );

  return (
    <div
      ref={panelRef}
      data-testid={PROTOCOL_TEST_IDS.criterionChooser}
      style={{ left: at.left, top: at.top, width: CHOOSER_WIDTH }}
      className="fixed z-30 rounded-md border-2 border-gray-200 bg-white p-2 shadow-md"
    >
      <TextField
        type="search"
        label="Rechercher un critère"
        hideLabel
        compact
        placeholder="Rechercher un critère"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
      />
      <div className="mt-1.5 max-h-64 overflow-y-auto">
        {offered.length === 0 ? (
          <p className="px-2 py-2 text-gray-500 text-small">aucun critère disponible</p>
        ) : (
          offered.map((card) => (
            <button
              key={card.id}
              type="button"
              data-testid={PROTOCOL_TEST_IDS.criterionChoice}
              data-criterion-id={card.id}
              onClick={() => onPick(card.id)}
              className="flex w-full cursor-pointer flex-wrap items-center gap-2 rounded-sm px-2 py-2 text-left text-small hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              <span className="min-w-0 font-semibold text-gray-900">{card.name}</span>
              {card.production === null ? null : (
                <VersionPill entity="critere" tag="production" version={card.production.number} />
              )}
              <VersionPill entity="critere" tag="development" version={card.development.number} />
            </button>
          ))
        )}
      </div>
    </div>
  );
}
